import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Field, Input, Label, Paragraph, Radio, Fieldset, ValidationMessage } from '@digdir/designsystemet-react';
import { useChangeUserinfo } from '../../../hooks/mutations/useChangeUserinfo';
import { useLanguageUrlSync } from '../../../hooks/useLanguageUrlSync';
import { SettingsSection } from '../../../components/TeamSettingsLayout';
import classes from '../SettingsPage.module.css';

const LANGUAGES = [
  { value: 'no', label: 'Norsk', lang: 'nb' },
  { value: 'en', label: 'English', lang: 'en' },
];

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type ProfileFormProps = { nickname: string; email: string };

/** Navn, e-post og språk */
export const ProfileForm = ({ nickname, email: savedEmail }: ProfileFormProps) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { changeLanguage } = useLanguageUrlSync();
  const [name, setName] = useState(nickname);
  const [email, setEmail] = useState(savedEmail);
  const [tried, setTried] = useState(false);
  const [saved, setSaved] = useState(false);
  const mutation = useChangeUserinfo();

  const nameError = tried && !name.trim() ? t('settings.profile.errors.name') : undefined;
  const emailError =
    tried && !email.trim()
      ? t('settings.profile.errors.email')
      : tried && !EMAIL.test(email.trim())
        ? t('settings.profile.errors.emailFormat')
        : undefined;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTried(true);
    setSaved(false);
    if (!name.trim() || !EMAIL.test(email.trim())) return;
    mutation.mutate(
      { nickname: name.trim(), email: email.trim() },
      {
        onSuccess: () => {
          setSaved(true);
          setTried(false);
          queryClient.invalidateQueries({ queryKey: ['GET_USERINFO'] });
          // E-posten står også i personverneksporten
          queryClient.invalidateQueries({ queryKey: ['GET_USER_DATA_EXPORT'] });
        },
      },
    );
  };

  return (
    <SettingsSection headingId="settings-profile" title={t('settings.profile.title')} description={t('settings.profile.description')}>
      <form className={classes.form} onSubmit={submit} noValidate aria-labelledby="settings-profile">
      {saved && (
        <Alert data-color="success" role="status">
          <Paragraph>{t('settings.profile.saved')}</Paragraph>
        </Alert>
      )}
      {mutation.isError && (
        <Alert data-color="danger" role="alert">
          <Paragraph>{t('settings.profile.saveFailed')}</Paragraph>
        </Alert>
      )}

      <Field className={classes.field}>
        <Label htmlFor="settings-name">{t('settings.profile.name')}</Label>
        <Input
          id="settings-name"
          autoComplete="name"
          value={name}
          aria-invalid={!!nameError}
          aria-describedby={nameError ? 'settings-name-error' : undefined}
          onChange={(event) => {
            setName(event.target.value);
            setSaved(false);
          }}
        />
        {nameError && <ValidationMessage id="settings-name-error">{nameError}</ValidationMessage>}
      </Field>

      <Field className={classes.field}>
        <Label htmlFor="settings-email">{t('settings.profile.email')}</Label>
        <Paragraph data-size="sm" id="settings-email-hint" className={classes.subtle}>
          {t('settings.profile.emailHint')}
        </Paragraph>
        <Input
          id="settings-email"
          type="email"
          autoComplete="email"
          value={email}
          aria-invalid={!!emailError}
          aria-describedby={emailError ? 'settings-email-hint settings-email-error' : 'settings-email-hint'}
          onChange={(event) => {
            setEmail(event.target.value);
            setSaved(false);
          }}
        />
        {emailError && <ValidationMessage id="settings-email-error">{emailError}</ValidationMessage>}
      </Field>

      <Fieldset className={classes.languages}>
        <Fieldset.Legend>{t('settings.profile.language')}</Fieldset.Legend>
        <Paragraph data-size="sm" className={classes.subtle}>
          {t('settings.profile.languageHint')}
        </Paragraph>
        <div className={classes.languageOptions}>
          {LANGUAGES.map((language) => (
            <Radio
              key={language.value}
              name="settings-language"
              value={language.value}
              lang={language.lang}
              label={language.label}
              className={classes.languageOption}
              checked={i18n.language === language.value}
              onChange={() => changeLanguage(language.value)}
            />
          ))}
        </div>
      </Fieldset>

      <div className={classes.cardFooter}>
        <Button type="submit" loading={mutation.isPending}>
          {t('settings.profile.save')}
        </Button>
      </div>
      </form>
    </SettingsSection>
  );
};
